"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import Table from "../modules/table/table"
import Badge from "../modules/Badge/Badge"
import Button from "../modules/Button/Button"
import Modal from "../modules/Modal/Moadal"
import styles from "../../../pages/inventory/InventoryDashboardPage.module.css"
import InfoCard from "../modules/InfoCard/InfoCard"
import StatusSelector from "../detail/StatusSelector"
import OrderPdfExporter from "./OrderPdfExporter"
import {
  getClientDropshipOrders,
  approveDropshipOrder,
  getWarehouseStatuses,
  updateDropshipOrderStatus,
  fetchByUrl,
} from "../../../services/inventory/detail"
import { parseDateSafe } from "../../../utils/dateFormatters"

const defaultStatusColors = {
  pending: { color: "#fef3c7", text: "#d97706", dotColor: "#d97706" },
  dropship_requested: { color: "#e0f2fe", text: "#0284c7", dotColor: "#0284c7" },
  shipped: { color: "#ddd6fe", text: "#6d28d9", dotColor: "#6d28d9" },
  delivered: { color: "#dcfce7", text: "#16a34a", dotColor: "#16a34a" },
  cancelled: { color: "#fee2e2", text: "#dc2626", dotColor: "#dc2626" },
}

const allowedTransitions = {
  pending: ["cancelled", "dropship_requested"],
  cancelled: ["pending"],
  dropship_requested: ["cancelled", "shipped", "delivered"],
  shipped: ["delivered"],
  delivered: [],
}

const normalize = (str) => (str || "").toString().trim().toLowerCase().replace(/\s+/g, " ")

const normalizeToKey = (str) => {
  return normalize(str).replace(/\s+/g, "_")
}

const transformDropshipResults = (raw = []) =>
  raw.map((order) => {
    const normalizedStatus = normalizeToKey(order.status)
    return {
      id: order.id,
      requestNumber: order.order_number,
      deviceType: order.name,
      quantity: 1,
      status: normalizedStatus,
      rawStatusLabel: order.status,
      location: order.location_name,
      requestDate: order.created_at,
      patientName: order.patient_name,
      patientAddress: `${order.shipping_address}, ${order.shipping_city}, ${order.shipping_state} ${order.shipping_zip_code}`,
      firstName: order.shipping_first_name,
      lastName: order.shipping_last_name,
      address: order.shipping_address,
      city: order.shipping_city,
      state: order.shipping_state,
      zipCode: order.shipping_zip_code,
      deviceName: order.name,
      gatewayOnly: order.ship_gateway_only,
      requiredSignature: order.required_signature,
      notes: order.notes,
      originalOrder: order,
    }
  })

const DropshipRequestsSection = ({
  selectedLocation = "all",
  searchTerm = "",
  statusFilter = "all",
  deviceTypeFilter = "all",
}) => {
  const [dropshipData, setDropshipData] = useState({ results: [], meta: {} })
  const [loading, setLoading] = useState(false)
  const [paginationLoading, setPaginationLoading] = useState(false)

  const [successModal, setSuccessModal] = useState({ isOpen: false, message: "" })
  const [dropshipDetailModal, setDropshipDetailModal] = useState({
    isOpen: false,
    selectedRequest: null,
  })
  const [statusColorMap, setStatusColorMap] = useState({})
  const [STATUS_OPTIONS, setStatusOptions] = useState([])
  const [apiLoading, setApiLoading] = useState(false)
  const printRef = useRef(null)

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const data = await getWarehouseStatuses()
        if (data && data.dropship_order_statuses) {
          const mappedColors = {}
          const options = data.dropship_order_statuses.map((statusLabel) => {
            const key = normalizeToKey(statusLabel)
            mappedColors[key] = defaultStatusColors[key] || {
              color: "#f3f4f6",
              text: "#374151",
              dotColor: "#6b7280",
            }
            return {
              label: statusLabel,
              value: key,
              ...mappedColors[key],
            }
          })
          setStatusColorMap(mappedColors)
          setStatusOptions(options)
        } else {
          const defaults = Object.keys(defaultStatusColors).map((k) => ({
            label: k.replace(/_/g, " "),
            value: k,
            ...defaultStatusColors[k],
          }))
          setStatusColorMap(defaultStatusColors)
          setStatusOptions(defaults)
        }
      } catch (err) {
        console.error("Error fetching warehouse statuses:", err)
        const defaults = Object.keys(defaultStatusColors).map((k) => ({
          label: k.replace(/_/g, " "),
          value: k,
          ...defaultStatusColors[k],
        }))
        setStatusColorMap(defaultStatusColors)
        setStatusOptions(defaults)
      }
    }
    fetchStatuses()
  }, [])

  const buildQueryParams = useCallback(() => {
    const params = {}
    if (selectedLocation && selectedLocation !== "all") {
      params.location_id = selectedLocation
    }
    if (deviceTypeFilter && deviceTypeFilter !== "all") {
      params.device_type = deviceTypeFilter
    }
    if (statusFilter && statusFilter !== "all") {
      params.status = normalizeToKey(statusFilter)
    }
    if (searchTerm && searchTerm.toString().trim() !== "") {
      params.order_number = searchTerm.trim()
    }
    return params
  }, [selectedLocation, deviceTypeFilter, statusFilter, searchTerm])

  const fetchDropshipOrders = useCallback(
    async (urlOrParams = {}) => {
      setPaginationLoading(true)
      try {
        let response
        if (typeof urlOrParams === "string") {
          response = await fetchByUrl(urlOrParams)
        } else {
          const params = { ...(urlOrParams || {}), ...buildQueryParams() }
          response = await getClientDropshipOrders(params)
        }

        if (response?.success) {
          const raw = response.data?.results ?? []
          const meta = response.data?.meta ?? {}
          const transformed = transformDropshipResults(raw)
          setDropshipData({ results: transformed, meta })
        } else if (Array.isArray(response?.results)) {
          const transformed = transformDropshipResults(response.results)
          setDropshipData({ results: transformed, meta: response.meta || {} })
        } else {
          setDropshipData({ results: [], meta: {} })
        }
      } catch (err) {
        console.error("Error fetching dropship orders:", err)
        setDropshipData({ results: [], meta: {} })
      } finally {
        setPaginationLoading(false)
      }
    },
    [buildQueryParams],
  )

  useEffect(() => {
    fetchDropshipOrders()
  }, [fetchDropshipOrders])

  const handleStatusChangeLocal = (requestId, newStatus) => {
    setDropshipData((prev) => ({
      ...prev,
      results: prev.results.map((r) => (r.id === requestId ? { ...r, status: newStatus } : r)),
    }))
    setDropshipDetailModal((prev) => ({
      ...prev,
      selectedRequest: prev.selectedRequest ? { ...prev.selectedRequest, status: newStatus } : null,
    }))
  }

  const handleStatusChange = async (requestId, newStatus) => {
    setApiLoading(true)
    try {
      const result = await updateDropshipOrderStatus(requestId, newStatus)
      if (result.success) {
        const statusFromServer = result.status ? normalizeToKey(result.status) : newStatus
        handleStatusChangeLocal(requestId, statusFromServer)
        setSuccessModal({ isOpen: true, message: result.message || "Status updated" })
      } else {
        const msg = result.message || result.error || "Failed to update status"
        setSuccessModal({ isOpen: true, message: msg })
      }
    } catch (err) {
      console.error("Error updating dropship status:", err)
      setSuccessModal({ isOpen: true, message: "Error updating status" })
    } finally {
      setTimeout(() => setSuccessModal({ isOpen: false, message: "" }), 3500)
      setApiLoading(false)
    }
  }

  const handleApproveRequest = async (requestId) => {
    setApiLoading(true)
    try {
      const result = await approveDropshipOrder(requestId)
      if (result.success) {
        handleStatusChangeLocal(requestId, result.status ? normalizeToKey(result.status) : "dropship_requested")
        setDropshipDetailModal({ isOpen: false, selectedRequest: null })
        setSuccessModal({ isOpen: true, message: result.message || "Forwarded" })
      } else {
        setDropshipDetailModal({ isOpen: false, selectedRequest: null })
        setSuccessModal({ isOpen: true, message: result.message || "Approval failed" })
      }
    } catch (err) {
      console.error("Error approving dropship:", err)
      setSuccessModal({ isOpen: true, message: "Error approving request" })
    } finally {
      setTimeout(() => setSuccessModal({ isOpen: false, message: "" }), 3500)
      setApiLoading(false)
    }
  }

  const handleRowClick = (request) => setDropshipDetailModal({ isOpen: true, selectedRequest: request })

  const handlePageChange = async (pageUrlOrNumber) => {
    setPaginationLoading(true)
    try {
      if (typeof pageUrlOrNumber === "string") {
        await fetchDropshipOrders(pageUrlOrNumber)
      } else if (typeof pageUrlOrNumber === "number") {
        const meta = dropshipData.meta || {}
        const base = meta.base_url || meta.base || meta.first || null
        if (base && typeof base === "string") {
          const url = `${base}${base.includes("?") ? "&" : "?"}page=${pageUrlOrNumber}`
          await fetchDropshipOrders(url)
        } else {
          await fetchDropshipOrders({ page: pageUrlOrNumber })
        }
      }
    } catch (err) {
      console.error("Error changing dropship page:", err)
    } finally {
      setPaginationLoading(false)
    }
  }

  const handlePrintLabel = (request) => {
    try {
      OrderPdfExporter.generate(request, statusColorMap, {
        type: "dropship",
        fileName: `dropship-${request.requestNumber || request.id}.pdf`,
      })
    } catch (err) {
      console.error("Error generating PDF:", err)
      setSuccessModal({ isOpen: true, message: "Failed to generate PDF" })
      setTimeout(() => setSuccessModal({ isOpen: false, message: "" }), 3000)
    }
  }

  const dropshipRequestColumns = [
    { key: "requestNumber", label: "Request #", alignLeft: true },
    { key: "deviceType", label: "Device Type", alignLeft: true },
    { key: "quantity", label: "Quantity", alignLeft: true },
    { key: "location", label: "Location", alignLeft: true },
    {
      key: "requestDate",
      label: "Request Date",
      alignLeft: true,
      render: (row) =>
        row.requestDate
          ? parseDateSafe(row.requestDate)?.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }) || "—"
          : "—",
    },
    { key: "patientName", label: "Patient Name", alignLeft: true },
    {
      key: "status",
      label: "Status",
      sorting: false,
      render: (row) => {
        const status = row.status
        const style = statusColorMap[status] || defaultStatusColors[status]

        return (
          <Badge
            variant={
              status === "delivered"
                ? "success"
                : status === "cancelled"
                  ? "danger"
                  : status === "pending"
                    ? "warning"
                    : "info"
            }
          >
            {status}
          </Badge>
        )
      },
    },
  ]

  const dropshipDetailModalFooter = dropshipDetailModal.selectedRequest && (
    <div className={styles.modal_footer_buttons}>
      <Button
        variant="secondary"
        size="small"
        loading={apiLoading}
        onClick={() => handleStatusChange(dropshipDetailModal.selectedRequest.id, "cancelled")}
        disabled={dropshipDetailModal.selectedRequest.status !== "pending"}
      >
        Cancel Request
      </Button>

      {/* <Button
        variant="secondary"
        size="small"
        onClick={() => handlePrintLabel(dropshipDetailModal.selectedRequest)}
        disabled={apiLoading}
        title="Download PDF"
      >
        Print Label
      </Button> */}

      <Button
        variant="primary"
        size="small"
        loading={apiLoading}
        onClick={() => handleApproveRequest(dropshipDetailModal.selectedRequest.id)}
        disabled={dropshipDetailModal.selectedRequest.status !== "pending"}
      >
        Forward to Tenovi
      </Button>
    </div>
  )

  return (
    <div className={styles.section}>
      <div title="Dropship Requests" className={styles.orders_card}>
        <Table
          columns={dropshipRequestColumns}
          data={{ results: dropshipData.results, meta: dropshipData.meta }}
          height={800}
          loading={loading || paginationLoading}
          pagination={true}
          meta={dropshipData.meta}
          paginationLoading={paginationLoading}
          onPageChange={handlePageChange}
          isRowClickable
          onRowClick={handleRowClick}
        />
      </div>

      {dropshipDetailModal.isOpen && dropshipDetailModal.selectedRequest && (
        <Modal
          title={`Dropship Request - ${dropshipDetailModal.selectedRequest.requestNumber}`}
          isOpen={dropshipDetailModal.isOpen}
          onClose={() => setDropshipDetailModal({ isOpen: false, selectedRequest: null })}
          width="800px"
          height="85vh"
          footer={dropshipDetailModalFooter}
        >
          <div id="dropship-print-area" ref={printRef} className={styles.detail_modal_content}>
            <InfoCard
              title="Shipping Information"
              data={[
                { label: "Patient Name", value: dropshipDetailModal.selectedRequest.patientName },
                { label: "First Name", value: dropshipDetailModal.selectedRequest.firstName },
                { label: "Last Name", value: dropshipDetailModal.selectedRequest.lastName },
                { label: "Address", value: dropshipDetailModal.selectedRequest.address },
                { label: "City", value: dropshipDetailModal.selectedRequest.city },
                { label: "State", value: dropshipDetailModal.selectedRequest.state },
                { label: "Zip Code", value: dropshipDetailModal.selectedRequest.zipCode },
                {
                  label: "Status",
                  value: (
                    <Badge
                      children={dropshipDetailModal.selectedRequest.status}
                    />
                  ),
                  // value: (
                  //   <StatusSelector
                  //     currentStatus={dropshipDetailModal.selectedRequest.status}
                  //     options={STATUS_OPTIONS.filter((opt) =>
                  //       allowedTransitions[dropshipDetailModal.selectedRequest.status]?.includes(opt.value),
                  //     )}
                  //     statusColorMap={statusColorMap}
                  //     onStatusChange={(newStatus) =>
                  //       handleStatusChange(dropshipDetailModal.selectedRequest.id, newStatus)
                  //     }
                  //   />
                  // ),
                },
              ]}
            />

            <InfoCard
              title="Device Information"
              data={[
                { label: "Device Name", value: dropshipDetailModal.selectedRequest.deviceName },
                { label: "Gateway Only", value: dropshipDetailModal.selectedRequest.gatewayOnly ? "True" : "False" },
                {
                  label: "Required Signature",
                  value: dropshipDetailModal.selectedRequest.requiredSignature ? "Yes" : "No",
                },
                { label: "Notes", value: dropshipDetailModal.selectedRequest.notes || "N/A" },
              ]}
            />
          </div>
        </Modal>
      )}

      {successModal.isOpen && (
        <Modal
          title="Success"
          isOpen={successModal.isOpen}
          onClose={() => setSuccessModal({ isOpen: false, message: "" })}
          width="400px"
          height="200px"
        >
          <div className={styles.success_modal_content}>
            <p>{successModal.message}</p>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default DropshipRequestsSection
